import { FC } from 'react'
import { Link } from 'react-router-dom'
import { Card as UiCard, Box, Panel } from '../../../ui'
import { currencyFormat } from '../../../helpers'
import moment from 'moment'
import { Flash } from '../../../../assets/icons'
import { useTranslation } from 'react-i18next'
import style from './style.m.less'
import { Collapse } from 'antd'

interface IOwnershipCardProps {
    data: any
}

const OwnershipCard: FC<IOwnershipCardProps> = ({ data }) => {
    const { t, i18n } = useTranslation()

    const {
        company_name,
        number,
        volume,
        period,
        start_date,
        end_date,
        inn,
        ogrn,
        snils,
        go_name,
        green_tools,
        created_at,
        third_party_company_ru,
        third_party_company_en,
        third_party_company_name_short,
        consumption_objects,
    } = data

    const totalGreenTools = green_tools?.length || 0

    const isCertificate = (toolType: string) => toolType === 'СП'
    const isGreenContract = (toolType: string) => toolType === 'ЗД'
    const isExternal = (toolType: string) => toolType === 'СИ'

    const toolTypePicker = (toolType: string): string => {
        if (isCertificate(toolType)) {
            return t('greenTools.ownershipCertificate.toolTypeCertificate')
        }
        if (isGreenContract(toolType)) {
            return t('greenTools.ownershipCertificate.toolTypeGreenContract')
        }
        if (isExternal(toolType)) {
            return t('greenTools.ownershipCertificate.toolTypeExternal', {
                company_name:
                    i18n.language === 'ru'
                        ? `${third_party_company_ru}`
                        : `${third_party_company_en}`,
                company_name_short: `${third_party_company_name_short}`,
                interpolation: { escapeValue: false },
            })
        }
        return toolType
    }

    return (
        <Box gap={'16px'}>
            <UiCard className={style.ownershipCard}>
                <div className={style.header}>
                    {t('greenTools.ownershipCertificate.generalInfo')}
                </div>
                <div className={style.wrapper}>
                    <Box direction="row">
                        <div className={style.leftPart}>
                            {t('greenTools.ownershipCertificate.consumer')}
                        </div>
                        <div>{company_name}</div>
                    </Box>
                    <Box direction="row">
                        <div className={style.leftPart}>
                            {t('greenTools.ownershipCertificate.totalVolume')}
                        </div>
                        <div>{`${currencyFormat(volume)} ${t('greenTools.kwh')}`}</div>
                    </Box>
                    {consumption_objects?.length > 0 && (
                        <Box direction="row">
                            <div className={style.leftPart}>
                                {t('greenTools.ownershipCertificate.consumptionObjects')}
                            </div>
                            <div>
                                {consumption_objects
                                    .map((obj: any) => obj.name)
                                    .filter(Boolean)
                                    .join(' / ')}
                            </div>
                        </Box>
                    )}
                    {green_tools?.length > 0 && (
                        <Box direction="row">
                            <div className={style.leftPart}>
                                {t('greenTools.ownershipCertificate.energyTypes')}
                            </div>
                            <div>
                                {[...new Set(green_tools.map((tool: any) => tool.energy_type_name))]
                                    .filter(Boolean)
                                    .join(' / ')}
                            </div>
                        </Box>
                    )}
                    <Box direction="row">
                        <div className={style.leftPart}>
                            {t('greenTools.ownershipCertificate.consumptionPeriod')}
                        </div>
                        <div>{period ? period : '-'}</div>
                    </Box>
                    <Box direction="row">
                        <div className={style.leftPart}>
                            {t('greenTools.ownershipCertificate.certificateNumber')}
                        </div>
                        <div>{number}</div>
                    </Box>
                    <Box direction="row">
                        <div className={style.leftPart}>
                            {t('greenTools.ownershipCertificate.certificateIssueDate')}
                        </div>
                        <div>{moment(created_at).format('DD.MM.YYYY')}</div>
                    </Box>
                </div>
                <div className={style.footnote}>
                    {t('greenTools.ownershipCertificate.disclaimer')}
                </div>
            </UiCard>

            {green_tools?.length > 0 && (
                <div className={style.greenToolsGrid}>
                    <Collapse className={style.impactCollapse}>
                        <Panel header={t('greenTools.consumptionData')} key="green-tools">
                            {green_tools.map((tool: any, index: number) => (
                                <UiCard key={index} className={style.greenToolCard}>
                                    <div className={style.header}>
                                        {t('greenTools.ownershipCertificate.consumptionInfo', {
                                            current: index + 1,
                                            total: totalGreenTools,
                                        })}
                                    </div>
                                    <div className={style.wrapper}>
                                        <Box direction="row">
                                            <div className={style.leftPart}>
                                                {t('greenTools.ownershipCertificate.toolType')}
                                            </div>
                                            <div>{toolTypePicker(tool.tool_type)}</div>
                                        </Box>
                                        <Box direction="row">
                                            <div className={style.leftPart}>
                                                {t('greenTools.ownershipCertificate.toolNumber')}
                                            </div>
                                            <div>{tool.number}</div>
                                        </Box>
                                        <Box direction="row">
                                            <div className={style.leftPart}>
                                                {isCertificate(tool.tool_type)
                                                    ? t(
                                                          'greenTools.ownershipCertificate.dateRepayment',
                                                      )
                                                    : t(
                                                          'greenTools.ownershipCertificate.attributeTransferDate',
                                                      )}
                                            </div>
                                            <div>{moment(tool.end_date).format('DD.MM.YYYY')}</div>
                                        </Box>
                                        <Box direction="row">
                                            <div className={style.leftPart}>
                                                {t('greenTools.ownershipCertificate.energyType')}
                                            </div>
                                            <div>{tool.energy_type_name}</div>
                                        </Box>
                                        <Box direction="row">
                                            <div className={style.leftPart}>
                                                {t(
                                                    'greenTools.ownershipCertificate.productionPeriod',
                                                )}
                                            </div>
                                            <div>{moment(tool.period).format('MMMM, YYYY')}</div>
                                        </Box>
                                        <Box direction="row">
                                            <div className={style.leftPart}>
                                                {t(
                                                    'greenTools.ownershipCertificate.generationAttributesVolume',
                                                )}
                                            </div>
                                            <div>{`${currencyFormat(tool.volume)} ${t(
                                                'greenTools.kwh',
                                            )}`}</div>
                                        </Box>
                                    </div>
                                    <Box margin="10px 0px" display="flex">
                                        <Link
                                            to={`/generator-card/${tool.generator_id}`}
                                            className={style.link}
                                        >
                                            <Box direction="row" align="center">
                                                <Flash />
                                                <Box margin="0px 5px">{tool.generator_name}</Box>
                                            </Box>
                                        </Link>
                                    </Box>
                                </UiCard>
                            ))}
                        </Panel>
                    </Collapse>
                </div>
            )}
        </Box>
    )
}

export default OwnershipCard
